import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonRefresher,
  IonRefresherContent,
  IonToast,
  RefresherEventDetail,
} from '@ionic/react';
import { peopleOutline } from 'ionicons/icons';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import ProFilterChips from '../../components/pro/ProFilterChips';
import ProMobileLegislatorCard from '../../components/pro/ProMobileLegislatorCard';
import ProTrackingDestinationLabel from '../../components/pro/ProTrackingDestinationLabel';
import ProTrackingWorkspacePicker from '../../components/pro/bottom-sheet/ProTrackingWorkspacePicker';
import { ProEmptyState, ProLoadingList, ProListToolbar } from '../../components/pro/primitives';
import {
  PRO_LEGISLATOR_DEFAULT_FILTERS,
  PRO_LEGISLATOR_FILTER_DEFS,
  PRO_LEGISLATOR_FILTER_KEYS,
  resolveProFilterDefs,
} from '../../components/pro/proFilterDefs';
import { useDebounce } from '../../hooks/useDebounce';
import { useProListUrlState } from '../../hooks/useProListUrlState';
import { useProTrackingWorkspace } from './useProTrackingWorkspace';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Legislator {
  id: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  partyAffiliation: string;
  chamber: string;
  district: string;
  title?: string;
  email?: string;
  workPhone?: string;
  sessionYear?: string;
  sponsoredBillsCount?: number;
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

const ProLegislatorsPage: React.FC = () => {
  const { t } = useTranslation('pro');
  const navigate = useNavigate();

  const LEGISLATOR_FILTERS = resolveProFilterDefs(PRO_LEGISLATOR_FILTER_DEFS, t);

  const {
    search,
    setSearch,
    activeFilters,
    handleFilterChange,
    handleClearFilters,
  } = useProListUrlState(PRO_LEGISLATOR_FILTER_KEYS, PRO_LEGISLATOR_DEFAULT_FILTERS);
  const [showError, setShowError] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const tracking = useProTrackingWorkspace();
  const { status, eligible, select } = tracking;
  const selectedWorkspace =
    tracking.status === 'selected' ? tracking.workspace : null;
  const workspaceId = selectedWorkspace?.id ?? null;

  const debouncedSearch = useDebounce(search, 300);
  const chamber = activeFilters.chamber;
  const party = activeFilters.party;

  // ── Legislators infinite query ────────────────────────────────────────────

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ['pro-legislators', { search: debouncedSearch, chamber, party }],
    async ({ pageParam = 1 }) => {
      const response = await api.get('/legislators', {
        params: {
          limit: PAGE_SIZE,
          page: pageParam,
          search: debouncedSearch || undefined,
          chamber: chamber || undefined,
          party: party || undefined,
        },
      });
      const result = response.data?.data || response.data;
      const legislators: Legislator[] = result?.legislators || (Array.isArray(result) ? result : []);
      const total: number = result?.pagination?.total || legislators.length;
      return { legislators, total, page: pageParam };
    },
    {
      staleTime: 30000,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        const loaded = lastPage.page * PAGE_SIZE;
        return loaded < lastPage.total ? lastPage.page + 1 : undefined;
      },
      onError: () => setShowError(true),
    },
  );

  // ── Tracked legislator IDs (selected workspace only) ─────────────────────

  const { data: trackedData } = useQuery(
    ['pro-tracked-legislators', workspaceId],
    async () => {
      if (!workspaceId) return [];
      const { data } = await api.get(
        `/workspace-legislator-tracking/workspace/${workspaceId}`,
      );
      return data.data || data || [];
    },
    { staleTime: 60000, enabled: !!workspaceId },
  );

  const trackedLegislatorIds: Set<string> = React.useMemo(() => {
    // needs-choice / none → no tracked indicators
    if (!workspaceId) return new Set<string>();
    const raw = Array.isArray(trackedData) ? trackedData : [];
    return new Set<string>(
      raw.map((item: unknown) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'legislator_id' in item) {
          return String((item as { legislator_id: unknown }).legislator_id);
        }
        if (item && typeof item === 'object' && 'id' in item) {
          return String((item as { id: unknown }).id);
        }
        return String(item);
      }),
    );
  }, [trackedData, workspaceId]);

  // ── Derived state ─────────────────────────────────────────────────────────

  const allLegislators = data?.pages.flatMap((p) => p.legislators) ?? [];
  const hasActiveFilters = chamber !== '' || party !== '';
  const isEmpty = !isLoading && allLegislators.length === 0;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    e.detail.complete();
  };

  const handleLegislatorClick = (leg: Legislator) => {
    navigate(`/pro/legislators/${leg.id}`);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="max-w-3xl mx-auto">
        <ProListToolbar
          value={search}
          placeholder={t('legislators.searchPlaceholder')}
          onSearchChange={setSearch}
        >
          <ProFilterChips
            filters={LEGISLATOR_FILTERS}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
          />
        </ProListToolbar>

        <div className="px-4 pt-1">
          <ProTrackingDestinationLabel
            status={status}
            workspace={selectedWorkspace}
            eligibleCount={eligible.length}
            onPress={() => setPickerOpen(true)}
            disabled={status === 'none' || status === 'context-ineligible'}
          />
        </div>

        <div className="px-4 pb-4 space-y-3 mt-2">
          {isLoading ? (
            <ProLoadingList variant="card" count={4} className="space-y-3" />
          ) : isEmpty ? (
            <ProEmptyState
              icon={peopleOutline}
              message={t('legislators.emptyState')}
              actionLabel={(hasActiveFilters || debouncedSearch) ? t('common.clearFilters') : undefined}
              onAction={(hasActiveFilters || debouncedSearch) ? () => handleClearFilters() : undefined}
            />
          ) : (
            allLegislators.map((leg) => (
              <ProMobileLegislatorCard
                key={leg.id}
                legislator={leg}
                isTracked={trackedLegislatorIds.has(leg.id)}
                onClick={() => handleLegislatorClick(leg)}
              />
            ))
          )}
        </div>

        {/* Infinite scroll */}
        <IonInfiniteScroll
          disabled={!hasNextPage}
          onIonInfinite={(e) => {
            fetchNextPage().then(() =>
              (e.target as HTMLIonInfiniteScrollElement).complete(),
            );
          }}
        >
          <IonInfiniteScrollContent loadingText={t('legislators.loadingMore')} />
        </IonInfiniteScroll>
      </div>

      {/* Error toast */}
      <ProTrackingWorkspacePicker
        isOpen={pickerOpen}
        eligible={eligible}
        selectedId={workspaceId}
        onSelect={(id) => {
          select(id);
          setPickerOpen(false);
        }}
        onDismiss={() => setPickerOpen(false)}
      />

      <IonToast
        isOpen={showError}
        onDidDismiss={() => setShowError(false)}
        message={t('legislators.errorMessage')}
        duration={4000}
        buttons={[
          {
            text: t('common.retry'),
            handler: () => {
              setShowError(false);
              refetch();
            },
          },
        ]}
        color="danger"
      />
    </>
  );
};

export default ProLegislatorsPage;
